import { supabase } from '../config/supabase'
import { isDemoMode } from '../utils/demoMode'
import { applySalonFilter } from '../utils/salonScope'

/**
 * Fetch all reviews with professional details
 */
export const getReviews = async (limit = 10, offset = 0) => {
  try {
    let query = supabase
      .from('reviews')
      .select(`
        *,
        professionals!left (
          id,
          name,
          role,
          image_url
        )
      `, { count: 'exact' })
      .eq('is_hidden', false)
    query = applySalonFilter(query)
    const { data, error, count } = await query
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)

    if (error) throw error
    return { data, error: null, count }
  } catch (error) {
    console.error('Error fetching reviews:', error)
    return { data: null, error, count: 0 }
  }
}

/**
 * Fetch reviews for a user's specific appointments
 */
export const getReviewsForUserAppointments = async (userId, appointmentIds = []) => {
  if (isDemoMode()) {
    return { data: [], error: null }
  }

  if (!userId || !Array.isArray(appointmentIds) || appointmentIds.length === 0) {
    return { data: [], error: null }
  }

  try {
    const { data, error } = await supabase
      .from('reviews')
      .select('appointment_id, professional_id, rating, review_text, created_at')
      .eq('user_id', userId)
      .in('appointment_id', appointmentIds)
      .eq('is_hidden', false)

    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error fetching reviews for appointments:', error)
    return { data: null, error }
  }
}

/**
 * Fetch reviews for a specific professional
 */
export const getReviewsByProfessional = async (professionalId, limit = 10) => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .select(`
        *,
        professionals (
          id,
          name,
          role,
          image_url
        )
      `)
      .eq('professional_id', professionalId)
      .eq('is_hidden', false)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching reviews by professional:', error)
    return { data: null, error }
  }
}

/**
 * Fetch reviews by a specific user
 */
export const getReviewsByUser = async (userId) => {
  try {
    if (isDemoMode()) {
      return { data: [], error: null }
    }

    const { data, error } = await supabase
      .from('reviews')
      .select(`
        *,
        professionals (
          id,
          name,
          role,
          image_url
        )
      `)
      .eq('user_id', userId)
      .eq('is_hidden', false)
      .order('created_at', { ascending: false })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching reviews by user:', error)
    return { data: null, error }
  }
}

/**
 * Create a new review
 */
export const createReview = async (reviewData) => {
  try {
    // Campaign demo: pretend the review was posted, keep it out of the database
    if (isDemoMode()) {
      console.log('🎭 DEMO MODE — review not saved to database')
      return {
        data: { ...reviewData, id: `demo-review-${Date.now()}`, created_at: new Date().toISOString(), _demo: true },
        error: null,
      }
    }

    const { data, error } = await supabase
      .from('reviews')
      .insert([reviewData])
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error creating review:', error)
    return { data: null, error }
  }
}

/**
 * Update a review
 */
export const updateReview = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error updating review:', error)
    return { data: null, error }
  }
}

/**
 * Delete a review
 */
export const deleteReview = async (id) => {
  try {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', id)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error deleting review:', error)
    return { error }
  }
}

/**
 * Check if user has reviewed a specific appointment (any professional)
 */
export const hasUserReviewedAppointment = async (userId, appointmentId) => {
  try {
    if (isDemoMode()) {
      return { hasReviewed: false, error: null }
    }

    const { data, error } = await supabase
      .from('reviews')
      .select('id')
      .eq('user_id', userId)
      .eq('appointment_id', appointmentId)
      .limit(1)

    if (error) throw error
    return { hasReviewed: Array.isArray(data) && data.length > 0, error: null }
  } catch (error) {
    console.error('Error checking review status:', error)
    return { hasReviewed: false, error }
  }
}

/**
 * Check if user already reviewed a specific professional for an appointment
 */
export const hasUserReviewedProfessionalForAppointment = async (
  userId,
  appointmentId,
  professionalId
) => {
  try {
    if (isDemoMode()) {
      return { hasReviewed: false, error: null }
    }

    if (!userId || !appointmentId || !professionalId) {
      return { hasReviewed: false, error: null }
    }

    const { data, error } = await supabase
      .from('reviews')
      .select('id')
      .eq('user_id', userId)
      .eq('appointment_id', appointmentId)
      .eq('professional_id', professionalId)
      .limit(1)

    if (error) throw error
    return { hasReviewed: Array.isArray(data) && data.length > 0, error: null }
  } catch (error) {
    console.error('Error checking professional review status:', error)
    return { hasReviewed: false, error }
  }
}

/**
 * Recompute and persist a professional's rating based on verified reviews
 */
export const recomputeAndPersistProfessionalRating = async (professionalId) => {
  try {
    if (!professionalId || isDemoMode()) return

    const { data: agg, error: aggError } = await supabase
      .from('reviews')
      .select('rating', { count: 'exact', head: false })
      .eq('professional_id', professionalId)
      .eq('is_verified', true)
      .eq('is_hidden', false)

    if (aggError) {
      console.error('Error aggregating reviews for professional:', aggError)
      return
    }

    if (!agg || agg.length === 0) {
      // No reviews → set rating to 0
      await supabase
        .from('professionals')
        .update({ rating: 0 })
        .eq('id', professionalId)
      return
    }

    const total = agg.reduce((sum, r) => sum + (Number(r.rating) || 0), 0)
    const average = total / agg.length
    const rounded = Math.round(average * 10) / 10

    // Persist to professionals table
    const { error: updateError } = await supabase
      .from('professionals')
      .update({ rating: rounded })
      .eq('id', professionalId)

    if (updateError) {
      console.error('❌ Error updating professional rating:', updateError)
      throw updateError
    } else {
      console.log(`✅ Successfully updated professional ${professionalId} rating to ${rounded}`)
    }
  } catch (e) {
    console.error('❌ Unexpected error persisting professional rating:', e)
    throw e
  }
}

/**
 * Get average rating for a professional
 * Only counts verified reviews (matches FreshaApp pattern)
 */
export const getProfessionalAverageRating = async (professionalId) => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .select('rating')
      .eq('professional_id', professionalId)
      .eq('is_verified', true) // Only count verified reviews
      .eq('is_hidden', false) // Exclude hidden reviews

    if (error) throw error
    
    if (!data || data.length === 0) {
      return { average: 0, count: 0, error: null }
    }

    const average = data.reduce((sum, review) => sum + review.rating, 0) / data.length
    return { average: parseFloat(average.toFixed(1)), count: data.length, error: null }
  } catch (error) {
    console.error('Error calculating average rating:', error)
    return { average: 0, count: 0, error }
  }
}

/**
 * PERFORMANCE OPTIMIZATION: Batch calculate ratings for all professionals in a single query
 * This replaces the N+1 query pattern where each professional's rating was calculated individually
 * @param {Array<string>} professionalIds - Array of professional IDs to calculate ratings for
 * @returns {Promise<Object>} Map of professionalId -> { average, count }
 */
export const batchGetProfessionalRatings = async (professionalIds = []) => {
  try {
    if (!professionalIds || professionalIds.length === 0) {
      return {}
    }

    // Fetch all verified reviews for all professionals in a single query
    const { data, error } = await supabase
      .from('reviews')
      .select('professional_id, rating')
      .in('professional_id', professionalIds)
      .eq('is_verified', true)
      .eq('is_hidden', false)

    if (error) throw error

    // Group by professional_id and calculate averages
    const ratingsMap = {}
    
    // Initialize all professionals with 0 rating
    professionalIds.forEach(id => {
      ratingsMap[id] = { average: 0, count: 0 }
    })

    // Group reviews by professional_id
    const reviewsByProfessional = {}
    if (data && data.length > 0) {
      data.forEach(review => {
        const profId = review.professional_id
        if (!reviewsByProfessional[profId]) {
          reviewsByProfessional[profId] = []
        }
        reviewsByProfessional[profId].push(review.rating)
      })
    }

    // Calculate averages for each professional
    Object.keys(reviewsByProfessional).forEach(profId => {
      const ratings = reviewsByProfessional[profId]
      const total = ratings.reduce((sum, rating) => sum + (Number(rating) || 0), 0)
      const average = total / ratings.length
      ratingsMap[profId] = {
        average: parseFloat(average.toFixed(1)),
        count: ratings.length
      }
    })

    return ratingsMap
  } catch (error) {
    console.error('Error batch calculating professional ratings:', error)
    // Return empty map on error - individual professionals will fallback to stored rating
    return {}
  }
}
